// Self-serve signup and onboarding: the rules the server enforces and the
// agent app shows, in one place so the two can't drift.
//
// Nothing here touches the database or the network. The server stores step
// state in account_onboarding (server/src/routes/onboarding.ts); the wizard
// and the dashboard checklist read it back through `buildChecklist`.

// ─── Signup ───

/** Lookup/uniqueness form of an email: trimmed and lower-cased, nothing more.
 *  Dots and `+tag` are kept — what they mean is the mail provider's business,
 *  and `agents.email` is citext, so the database agrees on case. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 200;

export type PasswordProblem = 'too-short' | 'too-long' | 'blank' | 'same-as-email';

/** The published password policy. The password is checked exactly as typed —
 *  never trimmed — so spaces count, and a password manager's output is kept. */
export function passwordProblem(password: string, email?: string): PasswordProblem | null {
  if ([...password].length < PASSWORD_MIN_LENGTH) return 'too-short';
  if ([...password].length > PASSWORD_MAX_LENGTH) return 'too-long';
  if (password.trim() === '') return 'blank';
  if (email && password.toLowerCase() === normalizeEmail(email)) return 'same-as-email';
  return null;
}

/** Bump when the terms or privacy policy change materially; signup records
 *  the version each owner accepted. */
export const TERMS_VERSION = '2026-10-29';

// ─── Wizard ───

/** Wizard steps, in order. Each is saved on its own and may be skipped. */
export const ONBOARDING_STEPS = ['profile', 'goal', 'team_size', 'platform', 'channels', 'widget', 'install'] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export const ONBOARDING_GOALS = ['support', 'leads', 'sales', 'automation', 'explore'] as const;
export const ONBOARDING_TEAM_SIZES = ['1-5', '6-10', '11-25', '26+'] as const;
export const ONBOARDING_PLATFORMS = ['shopify', 'wordpress', 'wix-squarespace', 'custom', 'none', 'other'] as const;
/** Channels a new account can say it is interested in — interest only, not a
 *  connection. Matches the channels Openline supports. */
export const ONBOARDING_CHANNELS = ['web', 'whatsapp', 'instagram', 'messenger', 'email'] as const;

export type OnboardingGoal = (typeof ONBOARDING_GOALS)[number];
export type OnboardingTeamSize = (typeof ONBOARDING_TEAM_SIZES)[number];
export type OnboardingPlatform = (typeof ONBOARDING_PLATFORMS)[number];
export type OnboardingChannel = (typeof ONBOARDING_CHANNELS)[number];

export interface OnboardingStepState {
  status: 'completed' | 'skipped';
  /** ISO timestamp of the last change. */
  at: string;
}
export type OnboardingSteps = Partial<Record<OnboardingStep, OnboardingStepState>>;

export function isOnboardingStep(value: unknown): value is OnboardingStep {
  return typeof value === 'string' && (ONBOARDING_STEPS as readonly string[]).includes(value);
}

/** Where the wizard resumes: the first step neither completed nor skipped,
 *  or null when every step has an answer. */
export function nextOnboardingStep(steps: OnboardingSteps): OnboardingStep | null {
  return ONBOARDING_STEPS.find((s) => !steps[s]) ?? null;
}

// ─── Widget install ───

/** `verified` comes only from the widget loading on a page (the server stamps
 *  sites.widget_seen_at); asking to check never sets it. */
export type InstallState = 'not_checked' | 'verified' | 'failed';

export function installState(site: { widgetSeenAt: Date | string | null; installCheckedAt: Date | string | null }): InstallState {
  if (site.widgetSeenAt) return 'verified';
  return site.installCheckedAt ? 'failed' : 'not_checked';
}

// ─── Checklist ───

export type ChecklistItemKey = 'verify_email' | OnboardingStep | 'test_message' | 'first_conversation';
export type ChecklistStatus = 'completed' | 'skipped' | 'pending';

export interface ChecklistItem {
  key: ChecklistItemKey;
  status: ChecklistStatus;
}

export interface ChecklistInput {
  emailVerified: boolean;
  steps: OnboardingSteps;
  install: InstallState;
  hasTestConversation: boolean;
  hasRealConversation: boolean;
}

/**
 * The dashboard setup checklist. A skipped step reads as skipped, never as
 * done. The install item is completed only by the real signal — marking the
 * step done in the wizard doesn't count — and the last item, a real visitor's
 * message, is activation.
 */
export function buildChecklist(input: ChecklistInput): ChecklistItem[] {
  const stepItem = (key: OnboardingStep): ChecklistItem => ({
    key,
    status: input.steps[key]?.status === 'completed' ? 'completed' : input.steps[key]?.status === 'skipped' ? 'skipped' : 'pending',
  });
  const install: ChecklistStatus =
    input.install === 'verified' ? 'completed' : input.steps.install?.status === 'skipped' ? 'skipped' : 'pending';
  return [
    { key: 'verify_email', status: input.emailVerified ? 'completed' : 'pending' },
    ...ONBOARDING_STEPS.filter((s) => s !== 'install').map(stepItem),
    { key: 'install', status: install },
    { key: 'test_message', status: input.hasTestConversation ? 'completed' : 'pending' },
    { key: 'first_conversation', status: input.hasRealConversation ? 'completed' : 'pending' },
  ];
}
